import React, { useEffect, useMemo, useState } from "react";

import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useGetUserPreferences, useUpdateUserPreferences } from "@api";

import { withController } from "@utils";

import { Button, Text } from "@ui/elements";
import { Slider } from "@ui/forms";
import { Stack } from "@ui/layout";
import { Colors } from "@ui/types";

import { toaster } from "@components";

type BetAmountFormFields = {
  kellyFraction: number;
};

const ControlledSlider = withController<BetAmountFormFields>()(Slider);

const DEFAULT_KELLY = 0.25;

export const BetAmount = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { data: userPreferenceData } = useGetUserPreferences();
  const { handleSubmit, watch, control, setValue } =
    useForm<BetAmountFormFields>({
      defaultValues: {
        kellyFraction: Number(userPreferenceData?.kellyFraction),
      },
    });
  const { mutateAsync } = useUpdateUserPreferences();

  useEffect(() => {
    if (userPreferenceData) {
      setValue("kellyFraction", userPreferenceData.kellyFraction);
    }
  }, [userPreferenceData]);

  const kellyFraction = watch("kellyFraction");

  const onSubmit = async (values: BetAmountFormFields) => {
    setLoading(true);
    setError("");

    try {
      await mutateAsync({ data: values });

      toaster.success({
        title: t("messages.successful-update"),
        message: t("messages.update-bet-amount-success"),
      });
    } catch (error) {
      setLoading(false);
      if (error instanceof Error) {
        return setError(error.message);
      }

      setError(String(error));
    } finally {
      setLoading(false);
    }
  };

  const kellyStrategy: Colors = useMemo(() => {
    if (kellyFraction <= 0.25) {
      return "info";
    }
    if (kellyFraction > 0.25 && kellyFraction <= 0.5) {
      return "warning";
    }
    if (kellyFraction >= 0.55) {
      return "danger";
    }

    return "text";
  }, [kellyFraction]);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack direction="column" gap="xl">
        {error && (
          <div className="notification is-danger is-light">{error}</div>
        )}
        <Stack direction="column">
          <Text
            variant="secondary"
            dangerouslySetInnerHTML={{ __html: t("bet-amount-intro-1") }}
          />
          <Text
            variant="secondary"
            dangerouslySetInnerHTML={{ __html: t("bet-amount-intro-2") }}
          />
        </Stack>

        <Stack direction="column" gap="xxs">
          <ControlledSlider
            name="kellyFraction"
            label={t("labels.kelly-fraction")}
            step={0.05}
            min={0.05}
            max={1}
            control={control}
            type={kellyStrategy}
          />
          {kellyStrategy === "info" && (
            <Text color="info">{t("risk-levels.conservative")}</Text>
          )}
          {kellyStrategy === "warning" && (
            <Text color="warning">{t("risk-levels.volatile")}</Text>
          )}
          {kellyStrategy === "danger" && (
            <Text color="danger">{t("risk-levels.highly-volatile")}</Text>
          )}
        </Stack>

        <Stack justify="between">
          {kellyFraction !== DEFAULT_KELLY ? (
            <Button
              type="button"
              variant="text"
              onClick={() => setValue("kellyFraction", DEFAULT_KELLY)}
            >
              {t("labels.reset-to-default")}
            </Button>
          ) : (
            <div />
          )}
          <Button variant="light" isLoading={loading}>
            {t("labels.update")}
          </Button>
        </Stack>
      </Stack>
    </form>
  );
};
